import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { AuthImage } from "@/components/AuthImage";
import { PeriodSelector } from "@/components/PeriodSelector";
import { km, fmtDate, hms, inRange } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Check, Pencil, RotateCcw, Images, CheckCircle2, Clock, Pause, Plus, History, Sigma, AlertTriangle } from "lucide-react";
import { OdometerValue } from "@/components/OdometerValue";
import { useKmAlertGate, useKmAlerts } from "@/components/KmAlertGate";

const isAvgKm = (r) => r?.km_source === "historical_average";
const AvgBadge = ({ r }) => (isAvgKm(r) ? (
  <span title={`Preenchido pela média das últimas ${r.km_average_basis || 30} rotas aprovadas (rota encerrada por 8h)`}
    className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-300" data-testid={`km-avg-badge-${r.id}`}>
    <Sigma className="h-3 w-3" />Média
  </span>
) : null);

// Monta o corpo do PUT de odômetros trocando um valor (km de cada trecho = fim − início, recalculado no quest-3).
const odometerBody = (route, legIdx, field, value) => {
  const legs = (route.legs?.length ? route.legs : [{ index: 0, start_odometer: route.start_odometer, end_odometer: route.end_odometer }])
    .map((l, i) => ({ index: l.index ?? i, start_odometer: l.start_odometer ?? null, end_odometer: l.end_odometer ?? null }));
  legs[legIdx] = { ...legs[legIdx], [field]: value };
  return { start_odometer: legs[0].start_odometer, end_odometer: legs[legs.length - 1].end_odometer, legs };
};

const statusBadge = (st) => {
  const map = {
    approved: "bg-emerald-500/15 text-emerald-300",
    pending: "bg-amber-500/15 text-amber-300",
    reproved: "bg-rose-500/15 text-rose-300",
  };
  const label = { approved: "Aprovado", pending: "Pendente", reproved: "Contestado" };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${map[st] || "bg-slate-800 text-slate-400"}`}>
      {label[st] || st || "—"}
    </span>
  );
};

function DriverRoutesDialog({ driver, routes, start, end, onClose, onSaved }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ date: todayStr, km: "" });
  const [busy, setBusy] = useState(false);

  const rows = routes
    .filter((r) => r.driver_id === driver.id && inRange(r.date || r.start_time, start, end))
    .sort((a, b) => String(b.date || b.start_time).localeCompare(String(a.date || a.start_time)));
  const tKm = rows.reduce((a, r) => a + (Number(r.corrected_km ?? r.reported_km ?? r.km) || 0), 0);
  const tDur = rows.reduce((a, r) => a + (Number(r.duration_seconds) || 0), 0);

  const addManual = async () => {
    if (!form.date) { toast.error("Informe a data"); return; }
    if (form.date > todayStr) { toast.error("Não é possível inserir data futura"); return; }
    if (form.km === "" || Number(form.km) < 0) { toast.error("Informe o KM rodado"); return; }
    setBusy(true);
    try {
      await api.post("/admin/routes/manual", { driver_id: driver.id, date: form.date, km: Number(form.km) });
      toast.success("Rota manual inserida");
      setAddOpen(false);
      setForm({ date: todayStr, km: "" });
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl border-slate-800 bg-slate-900" data-testid="km-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100"><History className="h-5 w-5 text-orange-400" /> Histórico de rotas — {driver.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Rotas do período, dia a dia. Registros inseridos manualmente aparecem marcados com o autor.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">{rows.length} rota(s) · {km(tKm)} · {hms(tDur)}</div>
          <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10" onClick={() => setAddOpen((v) => !v)} data-testid="km-add-toggle">
            <Plus className="h-4 w-4" /> Adicionar data
          </Button>
        </div>

        {addOpen && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-orange-500/30 bg-slate-950/50 p-3" data-testid="km-add-form">
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Data</Label>
              <Input type="date" max={todayStr} value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} data-testid="km-add-date" className="h-9 border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">KM rodado</Label>
              <Input type="number" min="0" step="0.1" value={form.km} onChange={(e) => setForm((s) => ({ ...s, km: e.target.value }))} data-testid="km-add-km" className="h-9 w-40 border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <Button className="h-9 bg-orange-500 text-white hover:bg-orange-600" disabled={busy} onClick={addManual} data-testid="km-add-save">Salvar registro</Button>
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm" data-testid="km-history-table">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Data</th>
                <th className="px-3 py-2 font-semibold">Hodômetro</th>
                <th className="px-3 py-2 font-semibold">KM inf.</th>
                <th className="px-3 py-2 font-semibold">KM corr.</th>
                <th className="px-3 py-2 font-semibold">Duração</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.length === 0 && <tr><td colSpan={6}><Empty label="Nenhuma rota no período" /></td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`km-history-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-200">
                    {fmtDate(r.date || r.start_time)}
                    {r.manual_entry && (
                      <span className="mt-0.5 flex w-fit items-center gap-1 rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-orange-300" data-testid={`km-history-manual-${r.id}`}>
                        <Pencil className="h-3 w-3" /> Manual{r.manual_entry_by_name ? ` · ${r.manual_entry_by_name}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{r.start_odometer ?? "—"} → {r.end_odometer ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-slate-200">{km(r.reported_km ?? r.km)}</td>
                  <td className="px-3 py-2 font-mono text-slate-200">{r.corrected_km != null ? km(r.corrected_km) : "—"}</td>
                  <td className="px-3 py-2 text-slate-400">{hms(r.duration_seconds)}</td>
                  <td className="px-3 py-2">{statusBadge(r.audit_status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditoriaKM() {
  const { data, loading, error, refetch } = useFetch("/admin/routes");
  const [filter, setFilter] = useState("all");
  const [driver, setDriver] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [kmValue, setKmValue] = useState("");
  const [historyDriver, setHistoryDriver] = useState(null);
  const [gate, kmAlertDialog] = useKmAlertGate(data?.routes);
  const kmAlerts = useKmAlerts();

  const saveOdometer = async (legIdx, field, value) => {
    try {
      const { data: res } = await api.put(`/admin/routes/${photos.id}/odometers`, odometerBody(photos, legIdx, field, value));
      toast.success("Odômetro atualizado e km recalculado");
      const fresh = await refetch();
      const updated = res?.route || (fresh?.data?.routes || []).find((x) => x.id === photos.id);
      if (updated) setPhotos((p) => ({ ...p, ...updated }));
      return true;
    } catch (e) {
      toast.error(e?.response?.status === 404 || e?.response?.status === 405
        ? "O quest-3 ainda não tem o endpoint de edição de odômetro (PUT /admin/routes/{id}/odometers)."
        : apiError(e));
      return false;
    }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const routes = (data?.routes || []).filter((r) =>
    (filter === "all" ? true : (r.audit_status || "pending") === filter) &&
    (driver === "all" ? true : r.driver_id === driver) &&
    inRange(r.date || r.start_time, start, end),
  );
  const driverOpts = data?.drivers || [];

  const audit = async (route, body, key) => {
    setBusy(`${route.id}-${key}`);
    try {
      await api.post(`/admin/routes/${route.id}/audit`, body);
      toast.success(body.km_alert ? "Rota aprovada com alerta de km" : "Auditoria atualizada");
      await refetch();
      setAdjust(null);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const approve = (route, body, key) => gate(route, body.corrected_km, (info) =>
    audit(route, info ? { ...body, km_alert: true, km_alert_avg: Math.round(info.avg * 10) / 10 } : body, key));

  const openAdjust = (r) => {
    setKmValue(String(r.corrected_km ?? r.reported_km ?? r.km ?? ""));
    setAdjust(r);
  };

  const filters = [
    { k: "all", label: "Todas" },
    { k: "pending", label: "Pendentes" },
    { k: "approved", label: "Aprovadas" },
  ];

  return (
    <div className="space-y-5" data-testid="auditoria-km">
      <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="km-period" />
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`km-filter-${f.k}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === f.k ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            {f.label}
          </button>
        ))}
        <select value={driver} onChange={(e) => setDriver(e.target.value)} data-testid="km-filter-driver"
          className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200">
          <option value="all">Todos entregadores</option>
          {driverOpts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <span className="ml-auto self-center text-sm text-slate-500">{routes.length} rota(s)</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="w-full text-sm" data-testid="km-table">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Entregador</th>
              <th className="px-4 py-3 font-semibold">Data</th>
              <th className="px-4 py-3 font-semibold">Hodômetro</th>
              <th className="px-4 py-3 font-semibold">KM informado</th>
              <th className="px-4 py-3 font-semibold">KM corrigido</th>
              <th className="px-4 py-3 font-semibold">Duração</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {routes.length === 0 && (
              <tr><td colSpan={8}><Empty label="Nenhuma rota para exibir" /></td></tr>
            )}
            {routes.map((r) => (
              <tr key={r.id} className="hover:bg-slate-800/40">
                <td className="px-4 py-3 font-medium text-slate-100">
                  <button onClick={() => setHistoryDriver({ id: r.driver_id, name: r.driver_name })} data-testid={`km-name-${r.id}`} className="underline-offset-2 hover:text-orange-300 hover:underline">
                    {r.driver_name || "—"}
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-300">{fmtDate(r.date || r.start_time)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">
                  {r.start_odometer ?? "—"} → {r.end_odometer ?? "—"}
                </td>
                <td className="px-4 py-3 font-mono text-slate-200">{km(r.reported_km ?? r.km)}<AvgBadge r={r} /></td>
                <td className="px-4 py-3 font-mono text-slate-200">
                  {r.corrected_km != null ? km(r.corrected_km) : "—"}
                </td>
                <td className="px-4 py-3 text-slate-400">
                  {hms(r.duration_seconds)}
                  {r.pauses?.length ? (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300" data-testid={`km-pause-badge-${r.id}`}>
                      <Pause className="h-3 w-3" />{r.pauses.length}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  {statusBadge(r.audit_status)}
                  {(r.km_alert || kmAlerts.data?.[r.id]) && (
                    <span title="Aprovada mesmo com km 30% ou mais acima da média de 30 dias" data-testid={`km-alert-badge-${r.id}`}
                      className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                      <AlertTriangle className="h-3 w-3" />Alerta km
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-300 hover:text-white"
                      onClick={() => setPhotos(r)} data-testid={`km-photos-${r.id}`} title="Ver fotos">
                      <Images className="h-4 w-4" />
                    </Button>
                    {r.audit_status !== "approved" ? (
                      <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700"
                        disabled={busy === `${r.id}-approve`}
                        onClick={() => approve(r, { approved: true }, "approve")}
                        data-testid={`km-approve-${r.id}`}>
                        <Check className="h-4 w-4" /> Aprovar
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300"
                        disabled={busy === `${r.id}-reopen`}
                        onClick={() => audit(r, { reopen: true }, "reopen")}
                        data-testid={`km-reopen-${r.id}`}>
                        <RotateCcw className="h-4 w-4" /> Reabrir
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-300 hover:text-orange-200"
                      onClick={() => openAdjust(r)} data-testid={`km-adjust-${r.id}`} title="Ajustar KM">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Route details dialog (photos + pauses) */}
      <Dialog open={!!photos} onOpenChange={(o) => !o && setPhotos(null)}>
        <DialogContent className="max-w-2xl border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Detalhes da rota — {photos?.driver_name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {photos?.pauses?.length ? "Trechos e pausas registrados na rota." : "Fotos do hodômetro no início e no fim da rota."}
              {" "}Use o lápis para corrigir um odômetro; os km são recalculados.
            </DialogDescription>
            {isAvgKm(photos) && (
              <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-xs text-violet-200" data-testid="km-avg-notice">
                Rota encerrada automaticamente por 8h sem km: {km(photos.km)} preenchido pela média das últimas {photos.km_average_basis || 30} rotas aprovadas.
              </div>
            )}
          </DialogHeader>
          {photos && (() => {
            const legs = photos.legs || [];
            const pauses = photos.pauses || [];
            const hasPauses = pauses.length > 0 && legs.length > 1;
            if (!hasPauses) {
              return (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" /> Início · odômetro <OdometerValue value={photos.start_odometer} testId="km-odo-start" onSave={(v) => saveOdometer(0, "start_odometer", v)} /></div>
                    <AuthImage path={photos.start_photo} className="h-56 w-full rounded-lg object-cover" />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs text-slate-400"><CheckCircle2 className="h-3 w-3" /> Fim · odômetro <OdometerValue value={photos.end_odometer} testId="km-odo-end" onSave={(v) => saveOdometer(Math.max(0, (photos.legs?.length || 1) - 1), "end_odometer", v)} /></div>
                    <AuthImage path={photos.end_photo} className="h-56 w-full rounded-lg object-cover" />
                  </div>
                </div>
              );
            }
            const legKm = (l) => (l.start_odometer != null && l.end_odometer != null
              ? Math.max(0, Number(l.end_odometer) - Number(l.start_odometer)) : Number(l.km) || 0);
            const totalKm = legs.reduce((a, l) => a + legKm(l), 0);
            const last = legs.length - 1;
            return (
              <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1" data-testid="km-pauses-detail">
                <div className="flex items-center justify-between rounded-lg bg-slate-800 px-4 py-2.5">
                  <span className="text-sm font-semibold text-slate-200">Total geral ({legs.map((_, i) => `Trecho ${i + 1}`).join(" + ")})</span>
                  <span className="font-mono text-base font-bold text-white" data-testid="km-total-geral">{km(totalKm)}</span>
                </div>
                {legs.map((l, i) => (
                  <div key={i} className="space-y-3">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3" data-testid={`km-leg-${i}`}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <div className="mb-1 flex items-center gap-1 text-xs text-slate-400">
                            {i === 0 ? <><Clock className="h-3 w-3" /> Início da rota</> : <>Retorno da pausa {i}</>} · odômetro <OdometerValue value={l.start_odometer} testId={`km-odo-leg-${i}-start`} onSave={(v) => saveOdometer(i, "start_odometer", v)} />
                          </div>
                          <AuthImage path={l.start_photo} className="h-48 w-full rounded-lg object-cover" />
                        </div>
                        <div>
                          <div className="mb-1 flex items-center gap-1 text-xs text-slate-400">
                            {i === last ? <><CheckCircle2 className="h-3 w-3" /> Fim da rota</> : <><Pause className="h-3 w-3" /> Pausa {i + 1}</>} · odômetro <OdometerValue value={l.end_odometer} testId={`km-odo-leg-${i}-end`} onSave={(v) => saveOdometer(i, "end_odometer", v)} />
                          </div>
                          <AuthImage path={l.end_photo} className="h-48 w-full rounded-lg object-cover" />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-900 px-3 py-2">
                        <span className="text-sm text-slate-300">Trecho {i + 1}</span>
                        <span className="font-mono text-sm font-semibold text-slate-100" data-testid={`km-leg-km-${i}`}>{km(legKm(l))}</span>
                      </div>
                    </div>
                    {i < last && pauses[i] && (
                      <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-300" data-testid={`km-pause-${i}`}>
                        <Pause className="h-4 w-4" /> Pausa {i + 1}{pauses[i].reason ? ` — ${pauses[i].reason}` : ""}
                        <span className="ml-auto font-mono text-xs text-slate-300">{hms(pauses[i].seconds)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Ajustar KM</DialogTitle>
            <DialogDescription className="text-slate-400">Informe o KM corrigido e aprove a rota.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-400">
              {adjust?.driver_name} • informado {km(adjust?.reported_km ?? adjust?.km)}
            </p>
            <div className="space-y-2">
              <Label className="text-slate-300">KM corrigido</Label>
              <Input type="number" step="0.1" value={kmValue} onChange={(e) => setKmValue(e.target.value)}
                data-testid="km-adjust-input"
                className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setAdjust(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={busy === `${adjust?.id}-adjust`}
              onClick={() => approve(adjust, { approved: true, corrected_km: parseFloat(kmValue) }, "adjust")}
              data-testid="km-adjust-save">
              Salvar e aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {kmAlertDialog}
      {historyDriver && (
        <DriverRoutesDialog
          driver={historyDriver}
          routes={data?.routes || []}
          start={start}
          end={end}
          onClose={() => setHistoryDriver(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
